import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
} from "react-router-dom";
import axios from "axios";
import { API_BASE, getAuthHeader } from "./api/api";
import Login from "./components/Login";
import UserManagement from "./components/UserManagement";
import CaseManagement from "./components/CaseManagement";
import UserDashboard from "./components/UserDashboard";

const App = () => {
  const [auth, setAuth] = useState({
    token: localStorage.getItem("token"),
    role: localStorage.getItem("role"),
  });

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          localStorage.clear();
          setAuth({ token: null, role: null });
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const Layout = ({ children }) => {
    const [pendingCases, setPendingCases] = useState([]);
    const [isVisible, setIsVisible] = useState(true);

    const fetchPending = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/user/my-cases`,
          getAuthHeader(),
        );
        if (res.data && res.data.cases) {
          setPendingCases(res.data.cases.filter((c) => c.is_pending));
        }
      } catch (err) {
        console.error("Global alert fetch failed");
      }
    };

    const handleDismiss = () => {
      setIsVisible(false);
      setTimeout(() => {
        setIsVisible(true);
      }, 10000);
    };

    useEffect(() => {
      fetchPending();
      const interval = setInterval(fetchPending, 10000);
      return () => clearInterval(interval);
    }, []);

    return (
      <div className="min-h-screen bg-gray-50 font-sans">
        <nav className="bg-white shadow-sm p-4 flex justify-between items-center">
          <div className="flex gap-6 items-center">
            <span className="font-black text-blue-600 text-xl italic underline">
              CASE.SYS
            </span>
            {auth.role === "admin" ? (
              <>
                <Link to="/users" className="hover:text-blue-600 font-medium">
                  User Management
                </Link>
                <Link to="/cases" className="hover:text-blue-600 font-medium">
                  Case Management
                </Link>
              </>
            ) : (
              <Link to="/my-cases" className="hover:text-blue-600 font-medium">
                My Dashboard
              </Link>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Logged in as: {localStorage.getItem("username")}
            </span>
            <button
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
              className="bg-red-50 text-red-600 px-3 py-1 rounded border border-red-200 text-sm font-bold"
            >
              Logout
            </button>
          </div>
        </nav>

        <main className="p-8">{children}</main>

        {isVisible && pendingCases.length > 0 && (
          <div className="fixed bottom-5 right-5 z-[9999] w-72 bg-white border-2 border-red-600 shadow-2xl rounded-lg overflow-hidden animate-bounce-subtle">
            <div className="bg-red-600 text-white px-4 py-2 font-bold text-sm flex justify-between items-center">
              <span>ACTION REQUIRED</span>
              <div className="flex items-center gap-2">
                <span className="bg-white text-red-600 px-2 rounded-full text-xs">
                  {pendingCases.length}
                </span>
                <button
                  onClick={handleDismiss}
                  className="hover:bg-red-700 px-1.5 rounded transition-colors text-lg leading-none"
                  title="Dismiss for 10s"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="p-2 max-h-40 overflow-y-auto">
              {pendingCases.map((c) => (
                <div
                  key={c._id}
                  className="mb-1 p-2 bg-red-50 border-l-4 border-red-500 text-[11px] font-bold text-red-700 uppercase"
                >
                  {c.title}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            !auth.token ? <Login setAuth={setAuth} /> : <Navigate to="/" />
          }
        />
        <Route
          path="/users"
          element={
            auth.role === "admin" ? (
              <Layout>
                <UserManagement />
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/cases"
          element={
            auth.role === "admin" ? (
              <Layout>
                <CaseManagement />
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/my-cases"
          element={
            auth.token && auth.role !== "admin" ? (
              <Layout>
                <UserDashboard />
              </Layout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/"
          element={
            <Navigate to={auth.role === "admin" ? "/users" : "/my-cases"} />
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
